import { useCallback, useEffect, useMemo, useState } from "react";
import { mergeMetrics, computeDashboardOrdersStats } from "@/lib/liveMetrics";
import { useWS } from "@/contexts/WebSocketContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Lightning, Pause, Play, X, MagnifyingGlass, CaretRight, CaretDown, Shield, ShieldWarning, TrendUp, TrendDown } from "@phosphor-icons/react";
import RefreshButton from "@/components/layout/RefreshButton";
import PageShell from "@/components/layout/PageShell";
import PageHeader from "@/components/layout/PageHeader";
import GroupBadge from "@/components/GroupBadge";
import OrderModal from "@/components/OrderModal";
import HedgeSquareOffModal from "@/components/HedgeSquareOffModal";
import api, { fmtMoney, fmtNum, formatErr } from "@/lib/api";
import { downloadReport, PRESET_ACCOUNT_GROUPS } from "@/lib/accountGroups";

const COMMANDS = [
  { command: "buy_order", label: "Buy Order", icon: TrendUp, variant: "accent", section: "trade" },
  { command: "sell_order", label: "Sell Order", icon: TrendDown, variant: "accent", section: "trade" },
  { command: "custom_lots", label: "Custom Lot Order", icon: Lightning, section: "trade" },
  { command: "close_all", label: "Close All Trades", icon: X, variant: "danger", section: "trade" },
  { command: "close_buy", label: "Close Buys", section: "trade" },
  { command: "close_sell", label: "Close Sells", section: "trade" },
  { command: "emergency_close", label: "Emergency Close", icon: X, variant: "danger", section: "trade" },

  // Hedge Options
  { command: "hedge_account", label: "Full Hedge (100%)", icon: Shield, section: "hedge" },
  { command: "hedge_half", label: "Half Hedge (50%)", icon: Shield, section: "hedge" },
  { command: "hedge_double", label: "Double Hedge (200%)", icon: Shield, section: "hedge" },
  { command: "hedge_custom", label: "Custom Lot Hedge", icon: Shield, section: "hedge" },
  { command: "close_hedges", label: "Close All Hedges", section: "hedge" },

  { command: "disable_entries", label: "Disable New Entries", section: "entries" },
  { command: "enable_entries", label: "Enable New Entries", section: "entries" },
  { command: "disable_trading", label: "Disable Trading", section: "entries" },
  { command: "pause_ea", label: "Pause EA", icon: Pause, section: "ea" },
  { command: "resume_ea", label: "Resume EA", icon: Play, section: "ea" },
  { command: "kill_switch", label: "Kill Switch", icon: X, variant: "danger", section: "ea" },
];

const COMMAND_SECTIONS = [
  { id: "trade", label: "Trades" },
  { id: "hedge", label: "Hedge" },
  { id: "entries", label: "Entries" },
  { id: "ea", label: "EA" },
];

function CommandPanel({ selectedCount, busy, commandSection, onSectionChange, onExec }) {
  const sectionCommands = COMMANDS.filter((c) => c.section === commandSection);

  return (
    <aside className="cmd-command-panel">
      <div className="cmd-panel-header">
        <div className="flex items-center justify-between gap-1.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <Lightning size={13} weight="fill" className="text-app-accent shrink-0" />
            <span className="cmd-panel-title">Actions</span>
          </div>
          <span className="cmd-tab-count">{selectedCount}</span>
        </div>
      </div>

      <div className="cmd-target-strip">
        <span className="cmd-target-label">Targets</span>
        <span className={`cmd-target-value ${selectedCount > 0 ? "text-app-accent" : "text-app-muted"}`}>
          {selectedCount} <span className="hidden sm:inline">account{selectedCount === 1 ? "" : "s"}</span>
        </span>
      </div>

      <div className="cmd-section-tabs" role="tablist" aria-label="Command category">
        {COMMAND_SECTIONS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={commandSection === id}
            onClick={() => onSectionChange(id)}
            className={`cmd-section-tab ${commandSection === id ? "cmd-section-tab-active" : ""}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="cmd-actions">
        {sectionCommands.map(({ command, label, icon: Icon, variant }) => (
          <button
            key={command}
            type="button"
            onClick={() => onExec(command, {}, label)}
            disabled={busy || selectedCount === 0}
            data-testid={`bulk-${command}`}
            className={`cmd-action-btn ${
              variant === "danger"
                ? "cmd-action-btn--danger"
                : variant === "accent"
                ? "cmd-action-btn--accent"
                : ""
            }`}
          >
            <span className="cmd-action-label">{label}</span>
            {Icon && <Icon size={13} weight="bold" className="shrink-0" />}
          </button>
        ))}
      </div>

      <div className="cmd-panel-footnote">
        {selectedCount === 0 ? "Select accounts from the list" : `Ready to apply ${COMMAND_SECTIONS.find((s) => s.id === commandSection)?.label} commands`}
      </div>
    </aside>
  );
}

function CommandsTable({ groupedAccounts, expandedGroups, selected, onToggleExpand, onToggleGroup, onToggleAccount, flatAccounts = null }) {
  const showFlat = Array.isArray(flatAccounts);

  return (
    <div className="cmd-table-wrap table-scroll">
    <table className="cmd-table w-full" data-testid="commands-table">
      <thead className="sticky top-0 z-10">
        <tr>
          <th className="cmd-th cmd-th-check" />
          <th className="cmd-th cmd-th-check" />
          <th className="cmd-th cmd-th-client text-left">Client / Account</th>
          <th className="cmd-th cmd-th-num text-right hidden xl:table-cell">Start</th>
          <th className="cmd-th cmd-th-num text-right hidden xl:table-cell">Close</th>
          <th className="cmd-th cmd-th-num text-right hidden lg:table-cell">DD</th>
          <th className="cmd-th cmd-th-num text-right">Profit</th>
          <th className="cmd-th cmd-th-num text-right hidden md:table-cell">Float</th>
          <th className="cmd-th cmd-th-num text-right hidden sm:table-cell">Pos</th>
          <th className="cmd-th cmd-th-ea text-right">EA</th>
        </tr>
      </thead>
      <tbody>
        {showFlat ? (
          flatAccounts.length === 0 ? (
            <tr>
              <td colSpan={10} className="px-4 py-10 text-center font-mono-d text-xs text-app-muted">NO SELECTED ACCOUNTS</td>
            </tr>
          ) : (
            flatAccounts.map((a) => (
              <CommandAccountTableRow
                key={a.id}
                account={a}
                selected={selected.has(a.id)}
                onToggle={() => onToggleAccount(a.id)}
                flat
              />
            ))
          )
        ) : (
          groupedAccounts.map((section) => (
            <CommandGroupRows
              key={section.key}
              section={section}
              expanded={expandedGroups.has(section.key)}
              onToggleExpand={() => onToggleExpand(section.key)}
              selected={selected}
              onToggleGroup={onToggleGroup}
              onToggleAccount={onToggleAccount}
            />
          ))
        )}
      </tbody>
    </table>
    </div>
  );
}

function accountMetrics(m = {}) {
  const profit = m.profit_today ?? m.daily_pl ?? 0;
  const maxDd = m.max_drawdown_today ?? m.drawdown ?? 0;
  const floating = m.floating_pl ?? 0;
  return {
    profit,
    maxDd,
    floating,
    startBal: m.starting_balance ?? m.balance ?? 0,
    closeBal: m.closing_balance ?? m.balance ?? 0,
    equity: m.equity ?? 0,
    positions: m.open_positions ?? 0,
  };
}

function aggregateGroupMetrics(accounts) {
  let profit = 0;
  let floating = 0;
  let balance = 0;
  let equity = 0;
  let positions = 0;
  let maxDd = 0;
  let startBal = 0;
  let closeBal = 0;
  let withdrawals = 0;
  let running = 0;
  let paused = 0;
  let offline = 0;

  for (const a of accounts) {
    const m = a.metrics || {};
    profit += m.profit_today ?? m.daily_pl ?? 0;
    floating += m.floating_pl ?? 0;
    balance += m.balance ?? 0;
    equity += m.equity ?? 0;
    positions += m.open_positions ?? 0;
    withdrawals += m.withdrawals_today ?? 0;
    startBal += m.starting_balance ?? m.balance ?? 0;
    closeBal += m.closing_balance ?? m.balance ?? 0;
    const dd = m.max_drawdown_today ?? m.drawdown ?? 0;
    if (dd > maxDd) maxDd = dd;

    if (!m.ea_connected) offline += 1;
    else if (m.ea_running) running += 1;
    else paused += 1;
  }

  return {
    profit: Math.round(profit * 100) / 100,
    floating: Math.round(floating * 100) / 100,
    balance: Math.round(balance * 100) / 100,
    equity: Math.round(equity * 100) / 100,
    positions,
    maxDd,
    startBal: Math.round(startBal * 100) / 100,
    closeBal: Math.round(closeBal * 100) / 100,
    withdrawals: Math.round(withdrawals * 100) / 100,
    running,
    paused,
    offline,
  };
}

function PnlValue({ value, className = "" }) {
  const accent = value >= 0 ? "text-[#10B981]" : "text-[#FF3B30]";
  return <span className={`font-mono-d tabular-nums text-xs ${accent} ${className}`}>{fmtMoney(value)}</span>;
}

function EaStatusBadge({ m }) {
  return (
    <span className={`cmd-ea-badge ${
      !m.ea_connected ? "cmd-ea-badge--off" :
      m.ea_running ? "cmd-ea-badge--run" : "cmd-ea-badge--pause"
    }`}>
      {!m.ea_connected ? "Offline" : m.ea_running ? "Running" : "Paused"}
    </span>
  );
}

function CompactAccountCell({ account: a, showGroup = false }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 min-w-0">
        {showGroup && a.group ? <GroupBadge name={a.group} color={a.group_color} /> : null}
        <div className="font-mono-d text-xs font-bold text-app-text truncate">{a.account_name || "—"}</div>
      </div>
      <div className="font-mono-d text-[11px] tabular-nums text-app-muted">#{a.account_number}</div>
      {a.broker && <div className="text-[10px] text-app-subtle truncate">{a.broker}</div>}
    </div>
  );
}

function CommandAccountTableRow({ account: a, selected, onToggle, flat = false }) {
  const m = a.metrics || {};
  const metrics = accountMetrics(m);

  return (
    <tr
      className={`cmd-account-row hover:bg-app-hover/50 ${flat ? "" : ""}`}
      data-testid={`cmd-row-${a.account_number}`}
    >
      <td className="cmd-td cmd-td-check" />
      <td className="cmd-td cmd-td-check align-middle">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="accent-[#007AFF]"
        />
      </td>
      <td className={`cmd-td align-middle min-w-0 ${flat ? "" : "pl-2"}`}>
        <CompactAccountCell account={a} showGroup={flat} />
      </td>
      <td className="cmd-td cmd-td-num text-right hidden xl:table-cell">{fmtMoney(metrics.startBal)}</td>
      <td className="cmd-td cmd-td-num text-right hidden xl:table-cell">{fmtMoney(metrics.closeBal)}</td>
      <td className={`cmd-td cmd-td-num text-right hidden lg:table-cell ${metrics.maxDd > 8 ? "text-[#FF3B30]" : ""}`}>
        {fmtNum(metrics.maxDd)}%
      </td>
      <td className="cmd-td cmd-td-num text-right">
        <PnlValue value={metrics.profit} />
      </td>
      <td className="cmd-td cmd-td-num text-right hidden md:table-cell">
        <PnlValue value={metrics.floating} />
      </td>
      <td className="cmd-td cmd-td-num text-right hidden sm:table-cell">{metrics.positions}</td>
      <td className="cmd-td cmd-td-ea text-right">
        <EaStatusBadge m={m} />
      </td>
    </tr>
  );
}

function CommandGroupRows({
  section,
  expanded,
  onToggleExpand,
  selected,
  onToggleGroup,
  onToggleAccount,
}) {
  const agg = useMemo(() => aggregateGroupMetrics(section.accounts), [section.accounts]);
  const sectionIds = section.accounts.map((a) => a.id);
  const sectionSelected = sectionIds.filter((id) => selected.has(id)).length;
  const allSectionSelected = sectionIds.length > 0 && sectionSelected === sectionIds.length;
  const someSectionSelected = sectionSelected > 0 && !allSectionSelected;

  return (
    <>
      <tr
        data-testid={`cmd-group-${section.key}`}
        className={`cmd-group-row cursor-pointer transition-colors ${expanded ? "" : "hover:bg-app-hover/70"}`}
        onClick={onToggleExpand}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggleExpand(); } }}
        aria-expanded={expanded}
      >
        <td className="cmd-td cmd-td-check align-middle text-app-muted">
          {expanded ? <CaretDown size={13} weight="bold" /> : <CaretRight size={13} weight="bold" />}
        </td>
        <td className="cmd-td cmd-td-check align-middle" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={allSectionSelected}
            ref={(el) => { if (el) el.indeterminate = someSectionSelected; }}
            onChange={() => onToggleGroup(section.accounts)}
            className="accent-[#007AFF]"
            aria-label={`Select all in ${section.label}`}
          />
        </td>
        <td className="cmd-td align-middle min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            {section.group ? (
              <GroupBadge name={section.group} color={section.color} />
            ) : (
              <span className="font-mono-d text-xs font-bold uppercase text-app-muted">Unassigned</span>
            )}
          </div>
          <div className="cmd-group-meta">
            {section.accounts.length} account{section.accounts.length === 1 ? "" : "s"} · {agg.positions} pos
            <span className="hidden sm:inline"> · {agg.running} run · {agg.paused} pause · {agg.offline} off</span>
            {sectionSelected > 0 ? ` · ${sectionSelected} selected` : ""}
          </div>
        </td>
        <td className="cmd-td cmd-td-num text-right font-semibold hidden xl:table-cell">{fmtMoney(agg.startBal)}</td>
        <td className="cmd-td cmd-td-num text-right font-semibold hidden xl:table-cell">{fmtMoney(agg.closeBal)}</td>
        <td className={`cmd-td cmd-td-num text-right font-semibold hidden lg:table-cell ${agg.maxDd > 8 ? "text-[#FF3B30]" : ""}`}>
          {fmtNum(agg.maxDd)}%
        </td>
        <td className="cmd-td cmd-td-num text-right font-semibold">
          <PnlValue value={agg.profit} />
        </td>
        <td className="cmd-td cmd-td-num text-right font-semibold hidden md:table-cell">
          <PnlValue value={agg.floating} />
        </td>
        <td className="cmd-td cmd-td-num text-right font-semibold hidden sm:table-cell">{agg.positions}</td>
        <td className="cmd-td cmd-td-ea text-right">
          <span className="cmd-group-tag">Group</span>
        </td>
      </tr>

      {expanded && section.accounts.map((a) => (
        <CommandAccountTableRow
          key={a.id}
          account={a}
          selected={selected.has(a.id)}
          onToggle={() => onToggleAccount(a.id)}
        />
      ))}
    </>
  );
}

export default function Commands() {
  const { tick, cmdTick, liveMetrics, liveVersion } = useWS();
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [listTab, setListTab] = useState("clients");
  const [commandSection, setCommandSection] = useState("trade");
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [orderModalCommand, setOrderModalCommand] = useState("buy_order");

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await api.get("/accounts");
      setAccounts(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to load accounts");
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/commands", { params: { limit: 20 } });
      setHistory(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to load command history");
    }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts, tick]);
  useEffect(() => { loadHistory(); }, [loadHistory, cmdTick]);

  const displayAccounts = useMemo(
    () => accounts.map((a) => ({
      ...a,
      metrics: mergeMetrics(a.metrics, liveMetrics[a.id]),
    })),
    [accounts, liveMetrics, liveVersion],
  );

  const filteredAccounts = useMemo(() => {
    if (!search) return displayAccounts;
    return displayAccounts.filter((a) =>
      `${a.account_number} ${a.broker} ${a.account_name} ${a.group || ""}`.toLowerCase().includes(search.toLowerCase()),
    );
  }, [displayAccounts, search]);

  const groupedAccounts = useMemo(() => {
    const byKey = new Map();
    for (const a of filteredAccounts) {
      const key = a.group || "__unassigned__";
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          group: a.group,
          color: a.group_color,
          label: a.group || "Unassigned",
          accounts: [],
        });
      }
      byKey.get(key).accounts.push(a);
    }

    const presetOrder = PRESET_ACCOUNT_GROUPS.map((g) => g.name);
    const sections = [...byKey.values()];
    sections.sort((a, b) => {
      if (a.key === "__unassigned__") return 1;
      if (b.key === "__unassigned__") return -1;
      const ai = presetOrder.indexOf(a.group);
      const bi = presetOrder.indexOf(b.group);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.label.localeCompare(b.label);
    });
    return sections;
  }, [filteredAccounts]);

  const selectedAccounts = useMemo(
    () => filteredAccounts.filter((a) => selected.has(a.id)),
    [filteredAccounts, selected],
  );

  const toggle = (id) => { const ns = new Set(selected); ns.has(id) ? ns.delete(id) : ns.add(id); setSelected(ns); };
  const toggleAll = () => selected.size === filteredAccounts.length
    ? setSelected(new Set())
    : setSelected(new Set(filteredAccounts.map((a) => a.id)));

  const toggleGroup = (groupAccounts) => {
    const ids = groupAccounts.map((a) => a.id);
    const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
    const ns = new Set(selected);
    if (allSelected) ids.forEach((id) => ns.delete(id));
    else ids.forEach((id) => ns.add(id));
    setSelected(ns);
  };

  const toggleGroupExpand = (key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const [hedgeModalOpen, setHedgeModalOpen] = useState(false);
  const [singleHedgeTrade, setSingleHedgeTrade] = useState(null);
  const [hedgeModalMode, setHedgeModalMode] = useState("all");

  const allSelectedTrades = useMemo(() => {
    if (selected.size === 0) return [];
    const trades = [];
    for (const a of displayAccounts) {
      if (selected.has(a.id)) {
        const m = a.metrics || {};
        const open = m.open_trades || a.open_trades || [];
        for (const t of open) {
          trades.push({ ...t, account_id: t.account_id || a.id });
        }
      }
    }
    return trades;
  }, [displayAccounts, selected]);

  const overallHedgeStats = useMemo(() => {
    let count = 0;
    let lots = 0.0;
    let mtm = 0.0;
    if (selected.size === 0) {
      return { count: 0, lots: 0, mtm: 0 };
    }
    for (const a of displayAccounts) {
      if (selected.has(a.id)) {
        const m = a.metrics || {};
        const open = m.open_trades || a.open_trades || [];
        if (open.length > 0) {
          const hedges = open.filter(
            (t) => Boolean(t.is_hedge || t.magic === 90001 || String(t.magic) === "90001" || t.magic === 999999 || String(t.magic) === "999999" || ["HEDGE", "HDG", "HUB"].some((kw) => String(t.comment || "").toUpperCase().includes(kw)))
          );
          count += hedges.length;
          lots += hedges.reduce((s, t) => s + Number(t.lot || 0), 0);
          mtm += hedges.reduce((s, t) => s + (Number(t.profit || 0) + Number(t.swap || 0) + Number(t.commission || 0)), 0);
        } else {
          count += Number(m.hedge_positions || 0);
          lots += Number(m.hedge_lots || 0);
          mtm += Number(m.hedge_mtm || 0);
        }
      }
    }
    return {
      count,
      lots: Math.round(lots * 100) / 100,
      mtm: Math.round(mtm * 100) / 100,
    };
  }, [displayAccounts, selected]);

  const dashboardStats = useMemo(() => {
    const targetAccs = selected.size > 0
      ? displayAccounts.filter((a) => selected.has(a.id))
      : displayAccounts;
    return computeDashboardOrdersStats(targetAccs, liveMetrics);
  }, [displayAccounts, selected, liveMetrics, liveVersion]);

  const handleSquareOffDashboardOrders = async () => {
    const targetAccs = selected.size > 0
      ? displayAccounts.filter((a) => selected.has(a.id))
      : displayAccounts;
    const targetIds = targetAccs.map((a) => a.id);
    const countLabel = selected.size > 0 ? `${selected.size} selected account(s)` : `all ${displayAccounts.length} account(s)`;

    if (dashboardStats.dashboard_orders_count === 0) {
      toast.info("No open Dashboard orders (Magic 90001) found across " + countLabel);
      return;
    }

    if (!window.confirm(
      `SQUARE OFF DASHBOARD ORDERS (${countLabel})?\n\n` +
      `• Total Dashboard Orders: ${dashboardStats.dashboard_orders_count} (${dashboardStats.dashboard_buy_count} Buy / ${dashboardStats.dashboard_sell_count} Sell)\n` +
      `• Total Dashboard Lots: ${dashboardStats.dashboard_lots}\n` +
      `• Current Dashboard MTM: ${fmtMoney(dashboardStats.dashboard_mtm)}\n\n` +
      `⚠️ EA auto-trades (Magic 998877) will NOT be touched.\nOnly Dashboard manual & hedge orders will be closed.`
    )) {
      return;
    }

    setBusy(true);
    try {
      const { data } = await api.post("/dashboard/orders/square-off", {
        account_ids: targetIds,
      });
      if (data.successfullyClosed > 0) {
        toast.success(`Dashboard Square-Off: ${data.successfullyClosed} order(s) queued for closure`);
      } else if (data.totalFound === 0) {
        toast.info("No active Dashboard orders found to square off");
      } else {
        toast.warning(`Square-Off: ${data.successfullyClosed} closed, ${data.failed} failed`);
      }
      await Promise.all([loadAccounts(), loadHistory()]);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Failed to square off dashboard orders");
    } finally {
      setBusy(false);
    }
  };

  const targetAccountIds = useMemo(() => {
    return Array.from(selected);
  }, [selected]);

  const openOrderDialog = (cmd = "buy_order") => {
    if (selected.size === 0) return toast.error("Select at least one account");
    setOrderModalCommand(cmd);
    setOrderModalOpen(true);
  };

  const exec = async (command, params = {}, label = command, skipConfirm = false) => {
    if (selected.size === 0) return toast.error("Select at least one account");
    
    if (!skipConfirm && (command === "buy_order" || command === "sell_order" || command === "custom_lots" || command === "hedge_custom")) {
      openOrderDialog(command);
      return;
    }

    if (!skipConfirm && command === "close_hedges") {
      setSingleHedgeTrade(null);
      setHedgeModalMode("all");
      setHedgeModalOpen(true);
      return;
    }

    if (!skipConfirm && !window.confirm(`Apply "${label}" to ${selected.size} account${selected.size === 1 ? "" : "s"}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/accounts/bulk/command", { account_ids: [...selected], command, params });
      toast.success(`${label} queued for ${data.count ?? selected.size} account${selected.size === 1 ? "" : "s"}`);
      await Promise.all([loadAccounts(), loadHistory()]);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to send command");
    } finally {
      setBusy(false);
    }
  };

  const manualRefresh = async () => {
    await Promise.all([loadAccounts(), loadHistory()]);
    toast.success("Command center refreshed");
  };

  const handleExport = async (scope) => {
    try {
      await downloadReport(scope);
      toast.success(scope === "today" ? "Today's report downloaded" : "All-time report downloaded");
    } catch {
      toast.error("Failed to download report");
    }
  };

  const cancelCmd = async (commandId) => {
    if (!window.confirm("Cancel this pending command?")) return;
    try {
      await api.post(`/commands/${commandId}/cancel`);
      toast.success("Command cancelled");
      await loadHistory();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to cancel command");
    }
  };

  if (user?.role === "viewer") {
    return <PageShell><div className="text-app-muted font-mono-d text-xs uppercase tracking-widest">VIEWER ROLE · No command privileges</div></PageShell>;
  }

  return (
    <PageShell className="page-shell--commands">
      <PageHeader eyebrow="Multi-Account Bulk Ops" title="COMMAND CENTER" className="shrink-0 mb-3 lg:mb-4">
        <div className="flex flex-col items-stretch sm:items-end gap-2.5 w-full lg:w-auto">
          <span className="inline-flex self-end items-center gap-1.5 px-2.5 py-1 font-mono-d text-[10px] uppercase tracking-widest border border-app-accent/35 text-app-accent bg-app-accent-soft">
            <Lightning size={12} weight="fill" />
            {selected.size} selected
          </span>
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={() => handleExport("today")}
              className="btn-touch text-xs px-3 py-2 border border-app-border bg-app-surface hover:bg-app-hover font-mono-d uppercase tracking-wider"
            >
              Today XLS
            </button>
            <button
              type="button"
              onClick={() => handleExport("all_time")}
              className="btn-touch text-xs px-3 py-2 border border-app-border bg-app-surface hover:bg-app-hover font-mono-d uppercase tracking-wider"
            >
              All-Time XLS
            </button>
            <RefreshButton onRefresh={manualRefresh} testId="commands-refresh-btn" />
          </div>
        </div>
      </PageHeader>

      {/* Order Operations Quick Toolbar */}
      <div className="mb-3 p-2.5 bg-app-surface border border-app-border rounded-lg flex flex-wrap items-center justify-between gap-2 shrink-0">
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <Lightning size={16} weight="fill" className="text-app-accent shrink-0" />
            <span className="font-mono-d text-xs font-semibold text-app-text tracking-wider uppercase">
              Order Operations
            </span>
          </div>

          {/* Dedicated Dashboard Orders MTM live indicator */}
          <div className="flex items-center gap-2 px-2.5 py-1 bg-app-bg/80 border border-app-border rounded font-mono-d">
            <span className="text-[10px] uppercase tracking-wider text-app-subtle">Dashboard MTM:</span>
            <span className={`text-xs font-bold tabular-nums ${dashboardStats.dashboard_mtm >= 0 ? "text-[#10B981]" : "text-[#FF3B30]"}`}>
              {fmtMoney(dashboardStats.dashboard_mtm)}
            </span>
            <span className="text-[10px] text-app-muted">
              ({dashboardStats.dashboard_orders_count} ord · {dashboardStats.dashboard_lots}L)
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => openOrderDialog("buy_order")}
            disabled={busy || selected.size === 0}
            className="btn-touch text-xs px-3 py-1.5 border border-[#10B981]/40 bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25 disabled:opacity-40 font-mono-d uppercase tracking-wider font-bold flex items-center gap-1"
          >
            <TrendUp size={14} weight="bold" /> BUY ORDER…
          </button>
          <button
            type="button"
            onClick={() => openOrderDialog("sell_order")}
            disabled={busy || selected.size === 0}
            className="btn-touch text-xs px-3 py-1.5 border border-[#FF3B30]/40 bg-[#FF3B30]/15 text-[#FF3B30] hover:bg-[#FF3B30]/25 disabled:opacity-40 font-mono-d uppercase tracking-wider font-bold flex items-center gap-1"
          >
            <TrendDown size={14} weight="bold" /> SELL ORDER…
          </button>
          <button
            type="button"
            onClick={() => openOrderDialog("custom_lots")}
            disabled={busy || selected.size === 0}
            className="btn-touch text-xs px-2.5 py-1.5 border border-app-accent/40 bg-app-accent-soft text-app-accent hover:opacity-90 disabled:opacity-40 font-mono-d uppercase tracking-wider font-semibold"
          >
            Custom Lot…
          </button>

          {/* 1-Click Square-Off for ONLY Dashboard Orders (Magic 90001) */}
          <button
            type="button"
            onClick={handleSquareOffDashboardOrders}
            disabled={busy || dashboardStats.dashboard_orders_count === 0}
            className="btn-touch text-xs px-3 py-1.5 border border-[#FF3B30] bg-[#FF3B30]/20 text-[#FF3B30] hover:bg-[#FF3B30]/35 disabled:opacity-30 font-mono-d uppercase tracking-wider font-bold flex items-center gap-1.5 shadow-sm shadow-[#FF3B30]/20"
            title="1-Click Square-Off for ONLY Dashboard orders (Magic 90001). EA grid auto-trades are NOT touched."
          >
            <ShieldWarning size={14} weight="fill" /> SQ OFF DASHBOARD
          </button>

          <button
            type="button"
            onClick={() => exec("close_all", {}, "Close All Trades")}
            disabled={busy || selected.size === 0}
            className="btn-touch text-xs px-2.5 py-1.5 border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF3B30] hover:bg-[#FF3B30]/20 disabled:opacity-40 font-mono-d uppercase tracking-wider font-semibold"
          >
            Close All
          </button>
          <button
            type="button"
            onClick={() => exec("close_buy", {}, "Close Buy Trades")}
            disabled={busy || selected.size === 0}
            className="btn-touch text-xs px-2.5 py-1.5 border border-app-border bg-app-bg text-app-muted hover:text-app-text disabled:opacity-40 font-mono-d uppercase tracking-wider"
          >
            Close Buys
          </button>
          <button
            type="button"
            onClick={() => exec("close_sell", {}, "Close Sell Trades")}
            disabled={busy || selected.size === 0}
            className="btn-touch text-xs px-2.5 py-1.5 border border-app-border bg-app-bg text-app-muted hover:text-app-text disabled:opacity-40 font-mono-d uppercase tracking-wider"
          >
            Close Sells
          </button>
        </div>
      </div>

      <div className="cmd-workspace cmd-workspace-fill">
        <section className="cmd-list-panel">
          <div className="cmd-tab-bar" role="tablist" aria-label="Account list views">
            <button
              type="button"
              role="tab"
              aria-selected={listTab === "clients"}
              onClick={() => setListTab("clients")}
              className={`cmd-tab ${listTab === "clients" ? "cmd-tab-active" : ""}`}
            >
              Clients
              <span className="cmd-tab-count">{groupedAccounts.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={listTab === "selected"}
              onClick={() => setListTab("selected")}
              className={`cmd-tab ${listTab === "selected" ? "cmd-tab-active" : ""}`}
            >
              Selected
              <span className="cmd-tab-count">{selected.size}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={listTab === "history"}
              onClick={() => setListTab("history")}
              className={`cmd-tab ${listTab === "history" ? "cmd-tab-active" : ""}`}
            >
              History
              <span className="cmd-tab-count">{history.length}</span>
            </button>
          </div>

          {listTab !== "history" && (
            <div className="cmd-list-toolbar">
              <div className="relative flex-1 min-w-0">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-app-muted pointer-events-none" />
                <input
                  id="commands-search"
                  placeholder="Search account, broker, client…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full bg-app-bg border border-app-border pl-8 pr-2.5 py-1.5 text-sm focus:outline-none focus:border-app-accent"
                />
              </div>
              <button
                type="button"
                onClick={toggleAll}
                data-testid="select-all-btn"
                className="shrink-0 px-2.5 py-1.5 border border-app-border bg-app-bg font-mono-d text-[10px] uppercase tracking-widest text-app-accent hover:bg-app-hover transition-colors"
              >
                {selected.size === filteredAccounts.length && filteredAccounts.length > 0 ? "None" : "All"}
              </button>
            </div>
          )}

          <div className="cmd-list-body table-scroll">
            {listTab === "history" ? (
              history.length === 0 ? (
                <div className="p-8 text-center font-mono-d text-xs text-app-muted">NO COMMANDS YET</div>
              ) : (
                <div>
                  {history.map((c) => (
                    <div key={c.id} className="cmd-history-row">
                      <div className="min-w-0 flex-1">
                        <div className="font-mono-d text-xs text-app-accent">{c.command}</div>
                        <div className="font-mono-d text-[10px] text-app-muted mt-0.5">· {c.account_id?.slice(0, 8)}</div>
                        {(c.status === "pending" || c.status === "delivered") && (
                          <div className="font-mono-d text-[10px] text-[#FF9500] mt-0.5">Waiting for EA</div>
                        )}
                        {c.ack_message && (
                          <div className="font-mono-d text-[10px] text-app-muted mt-0.5 break-words">{c.ack_message}</div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {(c.status === "pending" || c.status === "delivered") && (
                          <button
                            type="button"
                            onClick={() => cancelCmd(c.id)}
                            className="font-mono-d text-[10px] uppercase text-app-muted hover:text-app-text"
                          >
                            Cancel
                          </button>
                        )}
                        <span className={`font-mono-d text-[10px] uppercase ${
                          c.status === "executed" ? "text-[#10B981]" :
                          c.status === "pending" || c.status === "delivered" ? "text-[#FF9500]" :
                          c.status === "cancelled" ? "text-app-muted" : "text-[#FF3B30]"
                        }`}>{c.status}</span>
                        <span className="font-mono-d text-[10px] text-app-muted">{new Date(c.timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : filteredAccounts.length === 0 ? (
              <div className="p-8 text-center font-mono-d text-xs text-app-muted">NO ACCOUNTS</div>
            ) : (
              <CommandsTable
                groupedAccounts={groupedAccounts}
                expandedGroups={expandedGroups}
                selected={selected}
                onToggleExpand={toggleGroupExpand}
                onToggleGroup={toggleGroup}
                onToggleAccount={toggle}
                flatAccounts={listTab === "selected" ? selectedAccounts : null}
              />
            )}
          </div>
        </section>

        <CommandPanel
          selectedCount={selected.size}
          busy={busy}
          commandSection={commandSection}
          onSectionChange={setCommandSection}
          onExec={exec}
        />
      </div>

      <OrderModal
        isOpen={orderModalOpen}
        initialCommand={orderModalCommand}
        selectedCount={selected.size}
        busy={busy}
        onClose={() => setOrderModalOpen(false)}
        onConfirm={async (cmd, params, label) => {
          setOrderModalOpen(false);
          await exec(cmd, params, label, true);
        }}
      />

      <HedgeSquareOffModal
        isOpen={hedgeModalOpen}
        mode={hedgeModalMode}
        trade={singleHedgeTrade}
        selectedAccountIds={targetAccountIds}
        allTrades={allSelectedTrades}
        activeHedgesCount={overallHedgeStats.count}
        totalHedgeLots={overallHedgeStats.lots}
        overallHedgeMtm={overallHedgeStats.mtm}
        onClose={() => setHedgeModalOpen(false)}
        onSuccess={() => {
          loadAccounts();
          loadHistory();
        }}
      />
    </PageShell>
  );
}
